import { TrackLinkDirective } from '@analytics/events';
import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input } from '@angular/core';
import { ENVIRONMENT_CONFIG } from '@core/config-angular';
import {
  GenericRichTextDirective,
  getFieldValue,
  TextInterpolationPipe,
  TextInterpolationService,
} from '@core/jss-utils';
import { CrntIcon } from '@essent/current-web-angular';
import {
  getMGMSharedValue,
  getOrderOverviewFields,
  getYearlyCostsLabel,
  OfferUspsComponent,
} from '@innogy/become-a-customer/shared';
import {
  getFeatureFlags,
  getOrderCrossSellFormState,
  getOrderCrossSellProduct,
  getPromoCodePlans,
} from '@innogy/become-a-customer/state';
import { getSmartChargingAddonSelected } from '@innogy/retention/store';
import { hasMemberGetMember } from '@innogy/shared/member-get-member';
import { AutomationIdDirective } from '@innogy/utils-automation-id';
import {
  EuroCurrencyPipe,
  RoundedCurrencyPipe,
} from '@innogy/utils-deprecated';
import { SvgSkeletonLoaderComponent } from '@legacy/common-ui-shared';
import {
  RichTextWrapperComponent,
  ToolTipComponent,
} from '@legacy/common-ui-utility-components';
import { CSCaptureDirective } from '@legacy/utils-contentsquare';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { JssModule } from '@sitecore-jss/sitecore-jss-angular';

import { BaseOrderOverviewComponent } from '../base-order-overview/base-order-overview.component';
import { OrderOverviewBudgetBillComponent } from '../order-overview-shared/order-overview-budget-bill/order-overview-budget-bill.component';
import { OrderOverviewConsumptionComponent } from '../order-overview-shared/order-overview-consumption/order-overview-consumption.component';
import { OrderOverviewCostComponent } from '../order-overview-shared/order-overview-cost/order-overview-cost.component';
import { OrderOverviewHeaderComponent } from './order-overview-header/order-overview-header.component';

@Component({
  selector: 'essent-order-overview',
  templateUrl: './order-overview-essent.component.html',
  styleUrls: ['./order-overview-essent.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    GenericRichTextDirective,
    JssModule,
    CSCaptureDirective,
    TrackLinkDirective,
    GenericRichTextDirective,
    OrderOverviewHeaderComponent,
    OrderOverviewConsumptionComponent,
    OrderOverviewCostComponent,
    OrderOverviewBudgetBillComponent,
    AutomationIdDirective,
    EuroCurrencyPipe,
    RoundedCurrencyPipe,
    CSCaptureDirective,
    NgbModule,
    SvgSkeletonLoaderComponent,
    CrntIcon,
    RichTextWrapperComponent,
    ToolTipComponent,
    TextInterpolationPipe,
    OfferUspsComponent,
  ],
  providers: [RoundedCurrencyPipe],
})
export class OrderOverviewEssentComponent extends BaseOrderOverviewComponent {
  protected readonly store$ = inject(Store);
  private readonly environmentConfig = inject(ENVIRONMENT_CONFIG);
  private readonly currencyPipe = inject(RoundedCurrencyPipe);
  private readonly interpolateService = inject(TextInterpolationService);

  readonly retentionStickyView = input(false);

  public readonly fulfillmentCrossSellProgressiveFormState =
    this.store$.selectSignal(getOrderCrossSellFormState);
  public readonly fulfillmentCrossSellProduct = this.store$.selectSignal(
    getOrderCrossSellProduct
  );
  public readonly smartChargingAddonSelected = this.store$.selectSignal(
    getSmartChargingAddonSelected
  );
  public readonly promoCodePlans = this.store$.selectSignal(getPromoCodePlans);
  public readonly hasMemberGetMember =
    this.store$.selectSignal(hasMemberGetMember);
  public readonly termijnbedragVariantA = this.store$.selectSignal(
    getFeatureFlags('termijnbedragVariantA')
  );
  public readonly termijnbedragVariantB = this.store$.selectSignal(
    getFeatureFlags('termijnbedragVariantB')
  );

  public isCollapsed = true;

  constructor() {
    super();
    effect(() => {
      this.isCollapsed =
        this.variant() === 'accordion' ? false : this.isCollapsed;
    });
  }

  toggleCollapse() {
    this.isCollapsed = !this.isCollapsed;
  }

  readonly mgmSharedValue = computed(() =>
    getMGMSharedValue(this.offer(), this.environmentConfig.brand)
  );
  readonly yearlyCostLabel = computed(() =>
    getYearlyCostsLabel(
      ...getOrderOverviewFields(this.rendering()),
      this.offer()
    )
  );

  readonly displayMGM = computed(
    () => this.hasMemberGetMember() && !!this.mgmSharedValue()
  );

  readonly crossSellProductSelected = computed(
    () =>
      !!this.fulfillmentCrossSellProgressiveFormState()?.value
        ?.hasSelectedProduct
  );
  readonly crossSellLoyaltySelected = computed(
    () =>
      !!this.fulfillmentCrossSellProgressiveFormState()?.value
        ?.hasSelectedLoyaltyProgram
  );

  readonly promoCodeBadge = computed(() => {
    const promoCodeAmount = this.promoCodePlans()?.find(
      (plan) => plan.duration === this.offer()?.duration
    )?.amount;
    const label = getFieldValue(this.rendering(), 'PromoCodeLabel', '');

    let badge;
    if (promoCodeAmount && label) {
      const amount = this.currencyPipe.transform(
        promoCodeAmount,
        'EUR',
        'symbol',
        '1.0-2'
      );
      badge = this.interpolateService.interpolate(
        label,
        { amount },
        'plain_text'
      );
    }
    return badge;
  });
}
